import React, { useEffect, useState } from "react";
import { Save, FileText, Eye, EyeOff, Loader2, AlertCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";

const VARIANT_OPTIONS = [
  { value: "info",    label: "Info (doré)",     accent: "#C1A063" },
  { value: "warning", label: "Important (rouge)", accent: "#E54D2E" },
  { value: "promo",   label: "Promo (basilic)", accent: "#88A066" },
];

/**
 * Aperçu visuel du bloc tel qu'il s'affichera sur la boutique.
 * Reproduit fidèlement le rendu de <ShopCmsBanner> (couleurs, police, espacements).
 */
function BlockPreview({ block }) {
  const v = VARIANT_OPTIONS.find((o) => o.value === block.variant) || VARIANT_OPTIONS[0];
  const bgMap = {
    info:    "rgba(193,160,99,0.08)",
    warning: "rgba(229,77,46,0.08)",
    promo:   "rgba(136,160,102,0.08)",
  };
  if (!block.enabled) {
    return (
      <div className="px-5 py-4 border border-dashed text-xs italic text-[#A3A6A3] bg-[#FAFAF8]">
        Aperçu masqué — bloc désactivé. Activez l'interrupteur ci-dessus pour voir le rendu côté boutique.
      </div>
    );
  }
  return (
    <div
      className="px-5 py-4 border-l-2 rounded-r"
      style={{ borderColor: v.accent, background: "#0A0A0A" }}
    >
      <div className="px-1 py-1" style={{ background: bgMap[block.variant], borderRadius: 2 }}>
        <p className="text-[10px] uppercase tracking-[0.25em] font-semibold mb-2" style={{ color: v.accent }}>
          {block.title || "Titre du bloc"}
        </p>
        <p className="text-sm leading-relaxed" style={{ color: "#F2ECE4", whiteSpace: "pre-line" }}>
          {block.content || "(contenu vide — ne s'affichera pas sur la boutique)"}
        </p>
      </div>
    </div>
  );
}

export default function ShopCms() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [dirty, setDirty] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cms-blocks");
      setBlocks(data);
      setDirty({});
    } catch (e) {
      toast.error("Impossible de charger les blocs CMS");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateField = (slug, field, value) => {
    setBlocks((bs) => bs.map((b) => b.slug === slug ? { ...b, [field]: value } : b));
    setDirty((d) => ({ ...d, [slug]: true }));
  };

  const save = async (slug) => {
    const block = blocks.find((b) => b.slug === slug);
    if (!block) return;
    setSaving((s) => ({ ...s, [slug]: true }));
    try {
      const { data } = await api.put(`/cms-blocks/${slug}`, {
        title: block.title,
        content: block.content,
        variant: block.variant,
        enabled: block.enabled,
      });
      setBlocks((bs) => bs.map((b) => b.slug === slug ? data : b));
      setDirty((d) => { const { [slug]: _, ...rest } = d; return rest; });
      toast.success(`Bloc « ${block.title} » enregistré`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving((s) => ({ ...s, [slug]: false }));
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1F4E3D]" />
        <p className="mt-3 text-sm text-[#575A57]">Chargement des blocs CMS…</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="shop-cms-page">
      <div className="flex items-center gap-3 mb-2">
        <FileText className="w-6 h-6 text-[#1F4E3D]" />
        <h1 className="text-3xl font-bold text-[#1A1C1A]">Textes éditables boutique</h1>
      </div>
      <p className="text-sm text-[#575A57] mb-10 max-w-3xl">
        Modifiez les messages affichés sur la boutique sans toucher au code.
        Les changements sont visibles immédiatement après enregistrement (les visiteurs
        verront la nouvelle version au prochain chargement de page).
      </p>

      <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded p-3 mb-8 flex items-start gap-2 text-xs text-[#8C6B2F]">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          Astuce : utilisez des <strong>retours à la ligne</strong> pour structurer votre message —
          chaque saut de ligne dans la zone de texte est conservé tel quel sur la boutique.
        </div>
      </div>

      <div className="space-y-10">
        {blocks.map((block) => (
          <div
            key={block.slug}
            className="border border-[#E5E5E0] rounded-md bg-white p-6 space-y-5"
            data-testid={`cms-block-${block.slug}`}
          >
            {/* Header : titre interne + slug + toggle */}
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-[#F0F0EB]">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A6A3] font-mono mb-1">
                  Bloc · {block.slug}
                </div>
                <h2 className="text-lg font-semibold text-[#1A1C1A]">{block.title || "(sans titre)"}</h2>
              </div>
              <div className="flex items-center gap-2">
                {block.enabled
                  ? <Eye className="w-4 h-4 text-[#1F4E3D]" />
                  : <EyeOff className="w-4 h-4 text-[#A3A6A3]" />
                }
                <Switch
                  checked={block.enabled}
                  onCheckedChange={(v) => updateField(block.slug, "enabled", v)}
                  data-testid={`cms-enabled-${block.slug}`}
                />
                <span className="text-xs text-[#575A57] min-w-[60px]">
                  {block.enabled ? "Visible" : "Masqué"}
                </span>
              </div>
            </div>

            {/* Formulaire */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-1.5 block">Titre affiché</Label>
                <Input
                  value={block.title}
                  onChange={(e) => updateField(block.slug, "title", e.target.value)}
                  placeholder="Ex: Délais & fraîcheur"
                  data-testid={`cms-title-${block.slug}`}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-1.5 block">Style</Label>
                <select
                  value={block.variant}
                  onChange={(e) => updateField(block.slug, "variant", e.target.value)}
                  className="w-full h-9 border border-[#E5E5E0] rounded px-3 text-sm bg-white focus:outline-none focus:border-[#1F4E3D]"
                  data-testid={`cms-variant-${block.slug}`}
                >
                  {VARIANT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-1.5 block">Contenu</Label>
              <Textarea
                value={block.content}
                onChange={(e) => updateField(block.slug, "content", e.target.value)}
                rows={5}
                placeholder="Tapez votre message ici. Utilisez Entrée pour aller à la ligne."
                className="font-body text-sm leading-relaxed"
                data-testid={`cms-content-${block.slug}`}
              />
              <p className="text-[11px] text-[#A3A6A3] mt-1">
                {block.content.length} caractères · {block.content.split("\n").length} ligne(s)
              </p>
            </div>

            {/* Aperçu visuel */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-1.5 block">
                Aperçu (rendu côté boutique)
              </Label>
              <BlockPreview block={block} />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F0F0EB]">
              {dirty[block.slug] && (
                <span className="text-xs text-[#8C6B2F] italic mr-auto">Modifications non enregistrées</span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => load()}
                disabled={!dirty[block.slug]}
                data-testid={`cms-cancel-${block.slug}`}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Annuler
              </Button>
              <Button
                size="sm"
                onClick={() => save(block.slug)}
                disabled={!dirty[block.slug] || saving[block.slug]}
                className="bg-[#1F4E3D] hover:bg-[#163829] text-white"
                data-testid={`cms-save-${block.slug}`}
              >
                {saving[block.slug]
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Enregistrement…</>
                  : <><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</>
                }
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
